'use client';

import { c as _c } from "react/compiler-runtime";
import { ComponentType, useState } from 'react';
import { FaWhatsapp, FaFacebookF, FaLinkedinIn, FaInstagram, FaYoutube, FaTelegram, FaLink, FaCheck } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { MdEmail } from 'react-icons/md';
import { SocialProviderItem } from '@/lib/types/articleTemplate';
export interface DynamicSocialShareProps {
  items: SocialProviderItem[] | undefined;
  shareUrl: string;
  shareTitle: string;
  iconShape?: 'circle' | 'square';
}
function applyTokens(template: string, shareUrl: string, shareTitle: string): string {
  return template.replace(/\{shareUrl\}/g, encodeURIComponent(shareUrl)).replace(/\{shareTitle\}/g, encodeURIComponent(shareTitle));
}

// Real icon glyphs (inline SVG via react-icons — same library and providers
// as the legacy ArticleShareButtons, so both renderers look identical and
// nothing waits on a network fetch to paint). Builder-authored items set
// iconType: 'image' but rarely populate iconImageUrl, so matching by known
// provider id is the reliable path; falls back to the Builder's raw
// icon/iconImageUrl only for a provider this list doesn't recognize.
export const PROVIDER_ICONS: Record<string, ComponentType<{
  size?: number;
}>> = {
  whatsapp: FaWhatsapp,
  x: FaXTwitter,
  twitter: FaXTwitter,
  facebook: FaFacebookF,
  linkedin: FaLinkedinIn,
  email: MdEmail,
  instagram: FaInstagram,
  youtube: FaYoutube,
  telegram: FaTelegram
};
function CopyLinkButton(t0) {
  const $ = _c(13);
  const {
    item,
    shareUrl,
    shapeClass
  } = t0;
  const [copied, setCopied] = useState(false);
  let t1;
  if ($[0] !== shareUrl) {
    t1 = async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
    };
    $[0] = shareUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleCopy = t1;
  const t2 = copied ? "Copied!" : item.label;
  const t3 = copied ? "Copied!" : item.label;
  const t4 = `w-8 h-8 flex items-center justify-center text-FFFFFF ${shapeClass} transition-transform hover:scale-110`;
  const t5 = copied ? "#16a34a" : item.color;
  let t6;
  if ($[2] !== t5) {
    t6 = {
      backgroundColor: t5
    };
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== copied) {
    t7 = copied ? <FaCheck size={14} /> : <FaLink size={14} />;
    $[4] = copied;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== handleCopy || $[7] !== t2 || $[8] !== t3 || $[9] !== t4 || $[10] !== t6 || $[11] !== t7) {
    t8 = <button type="button" onClick={handleCopy} aria-label={t2} title={t3} className={t4} style={t6}>{t7}</button>;
    $[6] = handleCopy;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}

/**
 * Config-driven social share row — renders SocialProviderItem[] with
 * {shareUrl}/{shareTitle} token substitution in each provider's urlTemplate.
 * Sibling to the fixed 5-provider ArticleShareButtons (used by the legacy
 * per-story-template renderer), not a replacement for it.
 */
export default function DynamicSocialShare(t0) {
  const $ = _c(14);
  const {
    items,
    shareUrl,
    shareTitle,
    iconShape: t1
  } = t0;
  const iconShape = t1 === undefined ? "circle" : t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== iconShape || $[1] !== items || $[2] !== shareTitle || $[3] !== shareUrl) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const visible = (items || []).filter(_temp);
      if (visible.length === 0) {
        t4 = null;
        break bb0;
      }
      const shapeClass = iconShape === "square" ? "rounded" : "rounded-full";
      t2 = "flex items-center gap-2 p-3";
      let t5;
      if ($[7] !== shapeClass || $[8] !== shareTitle || $[9] !== shareUrl) {
        t5 = item_0 => {
          if (item_0.isCopyLink) {
            return <CopyLinkButton key={item_0.id} item={item_0} shareUrl={shareUrl} shapeClass={shapeClass} />;
          }
          const Icon = PROVIDER_ICONS[(item_0.reactIconKey || item_0.id).toLowerCase()];
          return <a key={item_0.id} href={applyTokens(item_0.urlTemplate, shareUrl, shareTitle)} target="_blank" rel="noopener noreferrer" aria-label={item_0.label} className={`w-8 h-8 flex items-center justify-center text-FFFFFF ${shapeClass} transition-transform hover:scale-110`} style={{
            backgroundColor: item_0.color
          }}>{Icon ? <Icon size={16} /> : item_0.iconType === "image" && item_0.iconImageUrl ? <img src={item_0.iconImageUrl} alt={item_0.label} className="w-4 h-4 object-contain" /> : <span className="text-12-tamil-700">{item_0.icon}</span>}</a>;
        };
        $[7] = shapeClass;
        $[8] = shareTitle;
        $[9] = shareUrl;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      t3 = visible.map(t5);
    }
    $[0] = iconShape;
    $[1] = items;
    $[2] = shareTitle;
    $[3] = shareUrl;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3) {
    t5 = <div className={t2}>{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function _temp(item) {
  return !item.hidden;
}